import { BasePlugin } from "@uppy/core";
import { detectConvertible, convertToJpeg, reportConvertFailure } from "@/utils/imageToJpeg";

const MAX_PARALLEL = 1;

export default class HeicToJpeg extends BasePlugin {
  constructor(uppy, opts) {
    super(uppy, { quality: 0.92, markCompressed: false, openEditorAfter: false, convert: convertToJpeg, ...opts });
    this.id = "HeicToJpeg";
    this.type = "modifier";
    this.jobs = new Map();
    this.queue = [];
    this.editors = new Map();
    this.sequence = 0;
    this.active = 0;
    this.installed = false;
    this.onFileAdded = this.onFileAdded.bind(this);
    this.onFileRemoved = this.onFileRemoved.bind(this);
    this.onThumbnail = this.onThumbnail.bind(this);
    this.prepareUpload = this.prepareUpload.bind(this);
  }

  isCurrent(id, token) {
    return this.installed && this.jobs.get(id)?.token === token && Boolean(this.uppy.getFile(id));
  }

  onFileAdded(file) {
    if (!this.installed || file.meta.ft206Converted || file.isRemote) return;
    const token = ++this.sequence;
    let resolve;
    const promise = new Promise(done => { resolve = done; });
    // autoProceed может войти в препроцессоры раньше чтения сигнатуры: обещание
    // должно быть в карте уже при возврате из синхронного file-added.
    const job = { token, promise, resolve, file };
    this.jobs.set(file.id, job);
    this.queue.push(job);
    this.drain();
  }

  // По одному: HEIC 48 Мп декодируется в ~0,7 ГБ памяти (спайк FT-206 STEP-01a), два
  // параллельных декодирования на слабом Android роняют вкладку. HEIC в пачке редки —
  // очередь почти всегда пуста, а обычные файлы проходят её за чтение 16 байт.
  drain() {
    while (this.installed && this.active < MAX_PARALLEL && this.queue.length) {
      const job = this.queue.shift();
      if (!this.isCurrent(job.file.id, job.token)) { job.resolve(); continue; }
      this.active++;
      Promise.resolve().then(() => this.run(job)).finally(() => {
        this.active--;
        job.resolve();
        this.drain();
      });
    }
  }

  async run(job) {
    const { file, token } = job;
    let progress = false;
    try {
      if (!this.isCurrent(file.id, token)) return;
      // Uppy допускает Blob без имени; декодер и последующий Compressor должны
      // получить File, иначе compressorjs восстановит старое расширение.
      const input = file.data.name === file.name ? file.data : new File([file.data], file.name, { type: file.type });
      progress = true;
      this.uppy.emit("preprocess-progress", this.uppy.getFile(file.id), { mode: "indeterminate" });
      if (!await detectConvertible(input) || !this.isCurrent(file.id, token)) return;
      const result = await this.opts.convert(input, { quality: this.opts.quality });
      if (!result || !this.isCurrent(file.id, token)) return;
      const current = this.uppy.getFile(file.id);
      const jpeg = result.file;
      this.uppy.setFileState(file.id, {
        data: jpeg, size: jpeg.size, type: "image/jpeg", name: jpeg.name, extension: "jpg",
        meta: {
          // XHRUpload берёт MIME части FormData из meta.type, а presign — из file.type.
          ...current.meta, name: jpeg.name, type: "image/jpeg", ft206Converted: true,
          ...(this.opts.markCompressed ? { ft152Compressed: true } : {}),
        },
        preview: undefined,
      });
      if (this.opts.openEditorAfter) {
        const timer = setTimeout(() => this.openEditor(file.id, token), 0);
        this.editors.set(file.id, { token, timer });
      }
      this.uppy.emit("thumbnail:request", this.uppy.getFile(file.id));
    } catch (e) {
      // Штатный конвертер сам сообщает об отказе; этот путь — для исключений
      // внедрённого конвертера и обработчиков Uppy, без байтов и имени файла.
      reportConvertFailure({ code: "uppy_convert", type: file.type, ext: file.extension, size: file.size });
    } finally {
      if (progress && this.isCurrent(file.id, token)) {
        this.uppy.emit("preprocess-complete", this.uppy.getFile(file.id));
      }
    }
  }

  async prepareUpload(fileIDs) {
    const jobs = fileIDs.map(id => this.jobs.get(id)).filter(Boolean);
    await Promise.all(jobs.map(job => job.promise));
  }

  openEditor(id, token) {
    const pending = this.editors.get(id);
    if (!pending || pending.token !== token) return;
    clearTimeout(pending.timer);
    this.editors.delete(id);
    if (this.isCurrent(id, token)) {
      this.uppy.getPlugin("Dashboard")?.openFileEditor(this.uppy.getFile(id));
    }
  }

  onThumbnail(file) {
    const pending = this.editors.get(file.id);
    // Старое превью могло завершиться уже после setFileState.
    if (pending && file.data === this.uppy.getFile(file.id)?.data) this.openEditor(file.id, pending.token);
  }

  onFileRemoved(file) {
    this.jobs.delete(file.id);
    const pending = this.editors.get(file.id);
    if (pending) clearTimeout(pending.timer);
    this.editors.delete(file.id);
    this.queue = this.queue.filter(job => {
      if (job.file.id !== file.id) return true;
      job.resolve();
      return false;
    });
  }

  install() {
    this.installed = true;
    this.uppy.addPreProcessor(this.prepareUpload);
    this.uppy.on("file-added", this.onFileAdded);
    this.uppy.on("file-removed", this.onFileRemoved);
    this.uppy.on("thumbnail:generated", this.onThumbnail);
  }

  uninstall() {
    this.installed = false;
    this.uppy.off("file-added", this.onFileAdded);
    this.uppy.off("file-removed", this.onFileRemoved);
    this.uppy.off("thumbnail:generated", this.onThumbnail);
    this.uppy.removePreProcessor(this.prepareUpload);
    this.editors.forEach(({ timer }) => clearTimeout(timer));
    this.editors.clear();
    this.queue.forEach(job => job.resolve());
    this.queue = [];
    this.jobs.clear();
  }
}
